/**
 * Git accounts beyond the built-in GitHub link (git sync Stage 1, card 6 —
 * "several accounts per provider; each workspace link stores provider id +
 * account id + remote").
 *
 * The account itself (provider, server, login, name, avatar) is kept with the
 * other linked accounts (accountsStore → accounts.json). The TOKEN never is:
 * it lives in the Apiboo Vault, as a hidden item, through the same Rust
 * command extensions use (secure_storage.rs), under a namespace no extension
 * can reach — a guest's calls are stamped with its own id by the host bridge —
 * and scoped to the signed-in Apiboo account, so after a user switch the
 * lookup misses instead of handing someone else's token over. (It used to be
 * the OS keychain; owner 26.10.2026: no keychain prompts. An old keychain
 * token moves into the Vault the first time it is read, in Rust.)
 *
 * The Vault must be unlocked to read or save a token. Saving and signing out
 * are user actions: they open the Vault gate and carry on after the unlock.
 * Reading never opens it — background status checks call it every few
 * minutes — it throws a typed error (`isVaultLockedError`) that the sync
 * surfaces turn into "Unlock the Vault to sync".
 *
 * The LIST is scoped the same way (owner, 07.10.2026): every account records
 * the space it was added in — `_local` when signed out of Apiboo, else the
 * Apiboo account id — and each space sees only its own. Signing in hides the
 * local accounts (their tokens live under `_local`), signing out brings them
 * back; a sign-out never deletes the local space's accounts or their links
 * (accountsStore.clearLinkedAccounts).
 */
import { invoke } from '@tauri-apps/api/core';
import { loadAccounts, saveAccounts } from '../accountsStore';
import { getStorageNamespace } from '../storageNamespace';
import { gitAccountSpace } from './gitAccountSpace';
import { isVaultLockedError, vaultLockedError, withVaultUnlocked, VAULT_LOCKED_SYNC_MESSAGE } from '../vaultGate';

export { VAULT_LOCKED_SYNC_MESSAGE };

/** The secure-storage namespace for git tokens — not an extension id. */
export const GIT_SECRET_NAMESPACE = 'apiboo.git';
const tokenKey = (accountId) => `token:${accountId}`;


/**
 * A server the user typed: https, or plain http only on this machine (a local
 * GitLab, a test server). Returns the normalized origin + path, no trailing slash.
 */
export function normalizeServer(input) {
  const raw = String(input || '').trim().replace(/\/+$/, '');
  let url;
  try {
    url = new URL(/^[a-z]+:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    throw new Error('That does not look like a server address.');
  }
  const local = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && local)) {
    throw new Error('Use an https:// address — a token must not travel unencrypted.');
  }
  if (url.username || url.password) throw new Error('Put the token in the token field, not in the address.');
  return `${url.protocol}//${url.host}${url.pathname.replace(/\/+$/, '')}`;
}

export const hostOf = (server) => {
  try {
    return new URL(server).host;
  } catch {
    return '';
  }
};

/** @returns {Promise<Array<{id, provider, server, login, name, avatarUrl, userId, addedAt}>>} */
export async function listGitAccounts(provider) {
  const space = getStorageNamespace();
  const all = ((await loadAccounts()).gitAccounts || []).filter((a) => gitAccountSpace(a) === space);
  return provider ? all.filter((a) => a.provider === provider) : all;
}

export async function getGitAccount(id) {
  return (await listGitAccounts()).find((a) => a.id === id) || null;
}

/**
 * The account's token, '' when none is stored. Never opens the Vault gate:
 * a locked (or not yet created) Vault throws a typed error instead.
 */
export async function getGitAccountToken(id) {
  if (!id) return '';
  try {
    return (await invoke('secure_storage_get', { extensionId: GIT_SECRET_NAMESPACE, key: tokenKey(id) })) || '';
  } catch (e) {
    if (isVaultLockedError(e)) {
      const err = vaultLockedError(e?.code || String(e?.message || e).trim());
      err.message = VAULT_LOCKED_SYNC_MESSAGE;
      throw err;
    }
    throw e;
  }
}

/**
 * Add (or refresh) an account after the provider confirmed the token.
 * `verify(server, token)` → { login, name, avatarUrl, userId } or throws.
 * `single`: the provider keeps one account per device (GitHub by token) — an
 * earlier one is signed out, its token leaves the Vault.
 * A locked Vault opens the gate (unlock or create), then the token is saved.
 */
export async function addGitAccount({ provider, server, token, verify, single = false }) {
  const base = normalizeServer(server);
  const secret = String(token || '').trim();
  if (!secret) throw new Error('Paste a personal access token.');
  const user = await verify(base, secret);
  if (!user?.login) throw new Error('The server did not say who this token belongs to.');
  const id = `${provider}:${hostOf(base)}:${user.login}`;
  await withVaultUnlocked(
    () => invoke('secure_storage_set', { extensionId: GIT_SECRET_NAMESPACE, key: tokenKey(id), value: secret }),
    'git-account'
  );
  const accounts = await loadAccounts();
  const space = getStorageNamespace();
  const all = accounts.gitAccounts || [];
  const mine = (a) => gitAccountSpace(a) === space;
  const replaced = single ? all.filter((a) => mine(a) && a.provider === provider && a.id !== id) : [];
  for (const old of replaced) {
    await invoke('secure_storage_delete', { extensionId: GIT_SECRET_NAMESPACE, key: tokenKey(old.id) });
  }
  // Other spaces' accounts are left exactly as they are — the same login may
  // be connected both signed out and inside an Apiboo account.
  const list = all.filter((a) => !(mine(a) && (a.id === id || replaced.includes(a))));
  const account = {
    id,
    space,
    provider,
    server: base,
    login: user.login,
    name: user.name || '',
    avatarUrl: user.avatarUrl || '',
    userId: user.userId ?? null,
    addedAt: Date.now(),
  };
  await saveAccounts({ ...accounts, gitAccounts: [...list, account] });
  return account;
}

/**
 * Sign out: the token leaves the Vault, the account leaves the list. A locked
 * Vault opens the gate first; closing it leaves everything as it was.
 */
export async function removeGitAccount(id) {
  await withVaultUnlocked(
    () => invoke('secure_storage_delete', { extensionId: GIT_SECRET_NAMESPACE, key: tokenKey(id) }),
    'git-account'
  );
  const accounts = await loadAccounts();
  const space = getStorageNamespace();
  await saveAccounts({
    ...accounts,
    gitAccounts: (accounts.gitAccounts || []).filter((a) => !(a.id === id && gitAccountSpace(a) === space)),
  });
}
